// AGC: FarmSetupWizard - First-time farm setup wizard
// AGC: Shows when user has no farms (first login after registration)
// AGC: ENHANCED: Now includes country/language selection and location options
// AGC: Uses dynamic theme variables for consistent styling

import React, { useState, useCallback, useEffect } from 'react'
import { clampDecimals } from '../../utils'
import { motion, AnimatePresence } from 'framer-motion'
// AGC: VoiceCalibrationPrompt no longer used — calibration is mandatory, no prompt needed
import {
  ChevronRight,
  ChevronLeft,
  MapPin,
  Ruler,
  Check,
  Loader2,
  Sprout,
  Home,
  Navigation,
  MessageCircle,
} from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { useKeyboardScroll } from '../../hooks/useKeyboardScroll'
import { useAuth } from '../../hooks/useAuth'
import { createField } from '../../services/fields'
import { useWizardTelemetry } from '../../hooks/useWizardTelemetry'
import { track } from '../../lib/analytics'
import { CountryLanguageStep } from './CountryLanguageStep'
import { LocationStepEnhanced } from './LocationStepEnhanced'
import { getBackendUrl } from '../../utils/backendUrl'
import { getPhoneFormatHint, type PhoneCountry } from '../../utils/phone'
import type { Field } from '../../types'
import toast from 'react-hot-toast'

// =============================================================================
// AGC: TYPES
// =============================================================================

interface WizardStepProps {
  onNext: () => void
  onBack?: () => void
  isFirst?: boolean
  isLast?: boolean
}

interface CountryData {
  code: string
  name: string
  language: 'pt' | 'es' | 'en'
  flag: string
  centerCoords: [number, number]
}

interface FarmData {
  name: string
  location: string
  totalArea: number
  coordinates: [number, number]
  country?: string
  countryData?: CountryData
  whatsappPhone: string
}

interface FarmSetupWizardProps {
  onComplete: (farm: Field) => void
  onSkip?: () => void
  onStartTour?: () => void // AGC: Callback to start welcome tour
  onStartLotWizard?: (farm: Field) => void // AGC: Chain into lot creation wizard
}

// AGC: Common input styles for high contrast
const inputClassName = "w-full px-4 py-3 bg-white text-gray-900 border border-gray-300 dark:border-gray-600 rounded-xl placeholder:text-gray-400 focus:ring-2 focus:ring-[var(--accent-primary)] focus:border-transparent dark:bg-gray-800 dark:text-white"

// =============================================================================
// AGC: STEP COMPONENTS
// =============================================================================

// AGC: Step 1 - Welcome
const WelcomeStep: React.FC<WizardStepProps> = ({ onNext }) => {
  const { t } = useTranslation()
  
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-6 text-center">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="w-24 h-24 rounded-full flex items-center justify-center mb-6 shadow-lg"
        style={{ background: 'var(--hero-gradient)' }}
      >
        <Sprout className="w-12 h-12 text-white" />
      </motion.div>
      
      <motion.h1
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-2xl font-bold text-gray-900 dark:text-white mb-3"
      >
        {t('wizard.welcome.title')}
      </motion.h1>
      
      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-gray-600 dark:text-gray-400 mb-8 max-w-xs"
      >
        {t('wizard.welcome.description')}
      </motion.p>
      
      <motion.button
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        onClick={onNext}
        className="px-8 py-3 text-white rounded-xl font-medium shadow-lg hover:opacity-90 transition-opacity flex items-center gap-2"
        style={{ backgroundColor: 'var(--accent-primary)' }}
      >
        {t('wizard.welcome.start')}
        <ChevronRight className="w-5 h-5" />
      </motion.button>
    </div>
  )
}

// AGC: Step 2b - WhatsApp number (used by farm agent to identify the account)
const WhatsAppStep: React.FC<WizardStepProps & {
  userId: string
  userName: string
  value: string
  onChange: (value: string) => void
  country?: string
}> = ({ onNext, onBack, userId, userName, value, onChange, country }) => {
  const { t } = useTranslation()
  const { refreshProfile } = useAuth()
  const { onFocus } = useKeyboardScroll()
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!value.trim() || isSaving) return
    setIsSaving(true)
    setError(null)
    try {
      const res = await fetch(`${getBackendUrl()}/api/v1/whatsapp/link-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, phone: value.trim(), userName }),
      })
      const data = await res.json()
      if (!data.success) {
        setError(data.message || t('wizard.whatsapp.saveError'))
        return
      }
      await refreshProfile()
      onNext()
    } catch {
      setError(t('wizard.whatsapp.saveError'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 px-6">
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col justify-center">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto flex-shrink-0 bg-green-100 dark:bg-green-900/30">
          <MessageCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
          {t('wizard.whatsapp.title')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4 text-center text-sm">
          {t('wizard.whatsapp.description')}
        </p>
        <input
          type="tel"
          value={value}
          onChange={(e) => { onChange(e.target.value); setError(null) }}
          onFocus={onFocus}
          placeholder={getPhoneFormatHint(country as PhoneCountry | undefined)}
          className={`${inputClassName} text-center text-lg`}
          autoFocus
          disabled={isSaving}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
          {t('wizard.whatsapp.helperText')}
        </p>
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400 mt-3 text-center">{error}</p>
        )}

        {/* AGC: Buttons inline after input so they stay above keyboard */}
        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onBack}
            disabled={isSaving}
            className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <ChevronLeft className="w-5 h-5" />
            {t('common.back')}
          </button>
          <button
            type="submit"
            disabled={!value.trim() || isSaving}
            className="flex-1 px-4 py-3 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--accent-primary)' }}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('common.saving')}
              </>
            ) : (
              <>
                {t('common.next')}
                <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  )
}

// AGC: Step 2 - Farm Name
const FarmNameStep: React.FC<WizardStepProps & { 
  value: string
  onChange: (value: string) => void 
}> = ({ onNext, onBack, value, onChange }) => {
  const { t } = useTranslation()
  const { onFocus } = useKeyboardScroll()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (value.trim()) {
      onNext()
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 px-6">
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col justify-center">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto flex-shrink-0"
          style={{ backgroundColor: 'var(--accent-light-bg)' }}
        >
          <Home className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
        </div>

        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
          {t('wizard.farmName.title')}
        </h2>

        <p className="text-gray-600 dark:text-gray-400 mb-4 text-center text-sm">
          {t('wizard.farmName.description')}
        </p>

        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          placeholder={t('wizard.farmName.placeholder')}
          className={`${inputClassName} text-center text-lg`}
          autoFocus
        />

        {/* AGC: Buttons inline after input so they stay above keyboard */}
        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
          >
            <ChevronLeft className="w-5 h-5" />
            {t('common.back')}
          </button>
          <button
            type="submit"
            disabled={!value.trim()}
            className="flex-1 px-4 py-3 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--accent-primary)' }}
          >
            {t('common.next')}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </form>
  )
}

// AGC: Step 3 - Location (kept for legacy, but now using LocationStepEnhanced)

// AGC: Step 4 - Total Area
const AreaStep: React.FC<WizardStepProps & {
  value: number
  onChange: (value: number) => void
}> = ({ onNext, onBack, value, onChange }) => {
  const { t } = useTranslation()
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (value > 0) {
      onNext()
    }
  }
  
  const { onFocus } = useKeyboardScroll()

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 px-6">
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col justify-center">
        <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mb-6 mx-auto flex-shrink-0">
          <Ruler className="w-8 h-8 text-purple-600 dark:text-purple-400" />
        </div>

        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
          {t('wizard.area.title')}
        </h2>

        <p className="text-gray-600 dark:text-gray-400 mb-4 text-center text-sm">
          {t('wizard.area.description')}
        </p>

        <div className="relative">
          <input
            type="number"
            value={value || ''}
            onChange={(e) => onChange(clampDecimals(parseFloat(e.target.value) || 0))}
            onFocus={onFocus}
            placeholder="0"
            min="0"
            step="0.1"
            className={`${inputClassName} pr-12 text-center text-2xl font-bold`}
            autoFocus
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 font-medium">
            ha
          </span>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
          {t('wizard.area.hint')}
        </p>

        {/* AGC: Buttons inline after input so they stay above keyboard */}
        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
          >
            <ChevronLeft className="w-5 h-5" />
            {t('common.back')}
          </button>
          <button
            type="submit"
            disabled={!value || value <= 0}
            className="flex-1 px-4 py-3 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--accent-primary)' }}
          >
            {t('common.next')}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </form>
  )
}

// AGC: Step 5 - Confirmation
const ConfirmationStep: React.FC<WizardStepProps & {
  farmData: FarmData
  isCreating: boolean
  onConfirm: () => void
}> = ({ onBack, farmData, isCreating, onConfirm }) => {
  const { t } = useTranslation()
  
  return (
    <div className="flex flex-col flex-1 min-h-0 px-6">
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col justify-center">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto flex-shrink-0"
          style={{ backgroundColor: 'var(--accent-light-bg)' }}
        >
          <Check className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
        </div>

        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
          {t('wizard.confirm.title')}
        </h2>

        <p className="text-gray-600 dark:text-gray-400 mb-6 text-center text-sm">
          {t('wizard.confirm.description')}
        </p>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 space-y-3 border border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <span className="text-gray-500 dark:text-gray-400 text-sm">{t('wizard.confirm.name')}</span>
            <span className="font-medium text-gray-900 dark:text-white">{farmData.name}</span>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700" />
          <div className="flex justify-between items-center">
            <span className="text-gray-500 dark:text-gray-400 text-sm">{t('wizard.confirm.location')}</span>
            <span className="font-medium text-gray-900 dark:text-white">{farmData.location}</span>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700" />
          <div className="flex justify-between items-center">
            <span className="text-gray-500 dark:text-gray-400 text-sm">{t('wizard.confirm.area')}</span>
            <span className="font-medium text-gray-900 dark:text-white">{farmData.totalArea} ha</span>
          </div>
          {farmData.coordinates[0] !== 0 && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-700" />
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400 text-sm">{t('wizard.confirm.coordinates')}</span>
                <span className="font-medium text-gray-900 dark:text-white text-xs">
                  {farmData.coordinates[0].toFixed(4)}, {farmData.coordinates[1].toFixed(4)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-3 py-4 flex-shrink-0">
        <button
          type="button"
          onClick={onBack}
          disabled={isCreating}
          className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <ChevronLeft className="w-5 h-5" />
          {t('common.back')}
        </button>
        <button
          onClick={onConfirm}
          disabled={isCreating}
          className="flex-1 px-4 py-3 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ backgroundColor: 'var(--accent-primary)' }}
        >
          {isCreating ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t('wizard.confirm.creating')}
            </>
          ) : (
            <>
              <Check className="w-5 h-5" />
              {t('wizard.confirm.createFarm')}
            </>
          )}
        </button>
      </div>
    </div>
  )
}

// =============================================================================
// AGC: MAIN WIZARD COMPONENT
// =============================================================================

export const FarmSetupWizard: React.FC<FarmSetupWizardProps> = ({ onComplete, onSkip, onStartTour, onStartLotWizard }) => {
  const { t } = useTranslation()
  const { getAuthToken, user, refreshProfile } = useAuth()
  // AGC: Wizard telemetry — tracks step views and completion for farm_setup funnel
  const { viewStep, complete: completeWizard } = useWizardTelemetry('farm_setup')
  // AGC: Track wizard start time so entity_created.duration_ms reflects the full session
  const wizardStartedAt = React.useRef<number>(Date.now())

  const [currentStep, setCurrentStep] = useState(0)
  const [isCreating, setIsCreating] = useState(false)

  // AGC: Farm data state - now includes country + WhatsApp phone for agent identification
  const [farmData, setFarmData] = useState<FarmData>({
    name: '',
    location: '',
    totalArea: 0,
    coordinates: [0, 0],
    country: '',
    countryData: undefined,
    whatsappPhone: '',
  })

  // AGC: STEP ORDER:
  // 0: Country & Language
  // 1: Welcome
  // 2: WhatsApp (farm agent identity)
  // 3: Farm Name
  // 4: Location
  // 5: Area
  // 6: Confirmation
  const steps = ['country', 'welcome', 'whatsapp', 'name', 'location', 'area', 'confirm']

  // AGC: Fire wizard_step_view each time the user advances to a new step
  useEffect(() => {
    const slug = steps[currentStep] ?? 'unknown'
    viewStep(currentStep, slug)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1)
    }
  }
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }
  
  const handleCreateFarm = useCallback(async () => {
    setIsCreating(true)

    try {
      const token = await getAuthToken()

      const newFarm = await createField({
        name: farmData.name,
        location: farmData.location,
        totalArea: farmData.totalArea,
        coordinates: farmData.coordinates[0] !== 0 ? farmData.coordinates : [-15.7975, -47.8919],
        plots: [],
      }, token)

      // AGC: Auto-enroll owner in all active sorteios (fire-and-forget — failure must not block wizard)
      if (token) {
        fetch(`${getBackendUrl()}/api/v1/sorteios/my/self-enroll`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        }).catch(() => {})
      }

      // AGC: createField went through verifyToken which triggers the backend's farmRole lazy
      // AGC: migration (self-signup → 'owner'). Pull that back into the Zustand store so useRBAC
      // AGC: sees owner on the dashboard — otherwise the user lands with stale farmRole=null and
      // AGC: gets demoted to 'worker' by the legacy fallback in useRBAC.ts:71.
      await refreshProfile()

      toast.success(t('wizard.success'))

      // AGC: Telemetry — wizard complete + farm entity created
      completeWizard()
      void track('entity_created', {
        entity_type: 'farm',
        source: 'wizard',
        duration_ms: Date.now() - wizardStartedAt.current,
      })

      onComplete(newFarm)

      // AGC: Chain into lot creation wizard if available
      if (onStartLotWizard) {
        onStartLotWizard(newFarm)
      } else if (onStartTour) {
        setTimeout(() => {
          onStartTour()
        }, 500)
      }
    } catch (error) {
      console.error('AGC: Error creating farm:', error)
      toast.error(t('wizard.error'))
    } finally {
      setIsCreating(false)
    }
  }, [farmData, getAuthToken, onComplete, onStartTour, onStartLotWizard, refreshProfile, t])
  
  // AGC: Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        // AGC: NEW - Country & Language selection
        return (
          <CountryLanguageStep
            onNext={(data) => {
              setFarmData(prev => ({
                ...prev,
                country: data.country,
                countryData: data.countryData,
                coordinates: data.countryData.centerCoords, // AGC: Set initial center
              }))
              handleNext()
            }}
            isFirst
          />
        )
      case 1:
        return <WelcomeStep onNext={handleNext} />
      case 2:
        return (
          <WhatsAppStep
            onNext={handleNext}
            onBack={handleBack}
            userId={user?.uid || ''}
            userName={user?.profile?.displayName || user?.displayName || ''}
            value={farmData.whatsappPhone}
            onChange={(whatsappPhone) => setFarmData(prev => ({ ...prev, whatsappPhone }))}
            country={farmData.country}
          />
        )
      case 3:
        return (
          <FarmNameStep
            onNext={handleNext}
            onBack={handleBack}
            value={farmData.name}
            onChange={(name) => setFarmData(prev => ({ ...prev, name }))}
          />
        )
      case 4:
        return (
          <LocationStepEnhanced
            location={farmData.location}
            coordinates={farmData.coordinates}
            countryCode={farmData.country || 'BR'}
            onLocationChange={(location) => setFarmData(prev => ({ ...prev, location }))}
            onCoordinatesChange={(coordinates) => setFarmData(prev => ({ ...prev, coordinates }))}
            onNext={handleNext}
            onBack={handleBack}
          />
        )
      case 5:
        return (
          <AreaStep
            onNext={handleNext}
            onBack={handleBack}
            value={farmData.totalArea}
            onChange={(totalArea) => setFarmData(prev => ({ ...prev, totalArea }))}
          />
        )
      case 6:
        return (
          <ConfirmationStep
            onBack={handleBack}
            farmData={farmData}
            isCreating={isCreating}
            onConfirm={handleCreateFarm}
            isLast
          />
        )
      default:
        return null
    }
  }
  
  return (
    <div className="h-[100dvh] bg-[#F4F5F7] dark:bg-slate-900 flex flex-col overflow-hidden" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      {/* AGC: Progress indicator — fixed height, never scrolls */}
      <div className="px-6 pt-8 pb-4 flex-shrink-0">
        <div className="flex justify-between items-center max-w-xs mx-auto">
          {steps.map((_, index) => (
            <React.Fragment key={index}>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  index <= currentStep
                    ? 'text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}
                style={index <= currentStep ? { backgroundColor: 'var(--accent-primary)' } : {}}
              >
                {index < currentStep ? <Check className="w-4 h-4" /> : index + 1}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-1 mx-2 rounded transition-colors ${
                    index < currentStep
                      ? ''
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                  style={index < currentStep ? { backgroundColor: 'var(--accent-primary)' } : {}}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* AGC: Step content — flex-1 + min-h-0 lets it shrink when keyboard opens */}
      <div className="flex-1 min-h-0 flex flex-col">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="flex-1 min-h-0 flex flex-col"
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* AGC: Skip button (only on welcome step) */}
      {currentStep === 1 && onSkip && (
        <div className="px-6 pb-8 flex-shrink-0">
          <button
            onClick={onSkip}
            className="w-full text-center text-gray-500 dark:text-gray-400 text-sm hover:text-gray-700 dark:hover:text-gray-300"
          >
            {t('wizard.skip')}
          </button>
        </div>
      )}

    </div>
  )
}

export default FarmSetupWizard
